import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  I18nManager,
  Modal,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getErrorMessage, parseError } from '../utils/errorHandler';
import { canManageZones } from '../utils/permissions';
import { useZones, useSites } from '../hooks/queries';
import { useCreateZone, useUpdateZone, useDeleteZone } from '../hooks/mutations';
import ErrorView from '../components/ErrorView';
import LoadingScreen from '../components/LoadingScreen';

interface Zone {
  id: string;
  name: string;
  description?: string;
  siteId?: string;
  site?: { id: string; name: string };
  createdAt: string;
}

interface Site {
  id: string;
  name: string;
}

export default function ZonesManagementScreen({ navigation }: any) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const {
    data: zones = [],
    isLoading,
    error: queryError,
    refetch,
    isRefetching,
  } = useZones(user?.orgId);
  const { data: sites = [] } = useSites(user?.orgId);
  const createZoneMutation = useCreateZone();
  const updateZoneMutation = useUpdateZone();
  const deleteZoneMutation = useDeleteZone();
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [zoneName, setZoneName] = useState('');
  const [zoneDescription, setZoneDescription] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const hasPermission = user?.roles ? canManageZones(user.roles) : false;
  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إدارة المناطق' : 'Zones Management',
      headerTintColor: '#ffffff',
      headerTitleStyle: {
        color: '#ffffff',
        fontWeight: '700',
      },
      headerRight: () => (
        <TouchableOpacity
          style={{ marginRight: 16 }}
          onPress={() => openModal()}
          accessible={true}
          accessibilityLabel={isArabic ? 'إضافة منطقة جديدة' : 'Add new zone'}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لفتح نموذج إضافة منطقة جديدة'
              : 'Double tap to open form to add a new zone'
          }
          accessibilityRole="button"
        >
          <Ionicons name="add-circle" size={28} color="#ffffff" />
        </TouchableOpacity>
      ),
    });

    if (!hasPermission) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية للوصول لهذه الصفحة'
          : 'You do not have permission to access this page.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }
  }, [navigation, theme, isArabic, hasPermission, user?.orgId]);

  const loadData = () => refetch();

  const openModal = (zone?: Zone) => {
    if (zone) {
      setEditingZone(zone);
      setZoneName(zone.name);
      setZoneDescription(zone.description || '');
      setSelectedSiteId(zone.siteId || zone.site?.id || '');
    } else {
      setEditingZone(null);
      setZoneName('');
      setZoneDescription('');
      setSelectedSiteId(sites.length > 0 ? sites[0].id : '');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingZone(null);
    setZoneName('');
    setZoneDescription('');
    setSelectedSiteId('');
  };

  const handleSave = async () => {
    if (!zoneName.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إدخال اسم المنطقة' : 'Please enter zone name',
      );
      return;
    }

    if (!selectedSiteId) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء اختيار الموقع' : 'Please select a site',
      );
      return;
    }

    try {
      setSaving(true);

      if (editingZone) {
        await updateZoneMutation.mutateAsync({
          zoneId: editingZone.id,
          data: {
            name: zoneName.trim(),
            description: zoneDescription.trim() || undefined,
            siteId: selectedSiteId,
          },
        });
        Alert.alert(
          isArabic ? 'نجح' : 'Success',
          isArabic ? 'تم تحديث المنطقة بنجاح' : 'Zone updated successfully',
        );
      } else {
        await createZoneMutation.mutateAsync({
          name: zoneName.trim(),
          description: zoneDescription.trim() || undefined,
          siteId: selectedSiteId,
          orgId: user?.orgId,
        });
        Alert.alert(
          isArabic ? 'نجح' : 'Success',
          isArabic ? 'تم إضافة المنطقة بنجاح' : 'Zone added successfully',
        );
      }

      closeModal();
    } catch (error) {
      const appError = parseError(error);
      Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (zone: Zone) => {
    Alert.alert(
      isArabic ? 'تأكيد الحذف' : 'Confirm Delete',
      isArabic ? `هل تريد حذف المنطقة "${zone.name}"؟` : `Delete zone "${zone.name}"?`,
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'حذف' : 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteZoneMutation.mutateAsync(zone.id);
              Alert.alert(
                isArabic ? 'نجح' : 'Success',
                isArabic ? 'تم حذف المنطقة' : 'Zone deleted',
              );
            } catch (error) {
              const appError = parseError(error);
              Alert.alert(
                isArabic ? 'خطأ' : 'Error',
                getErrorMessage(appError, isArabic ? 'ar' : 'en'),
              );
            }
          },
        },
      ],
    );
  };

  const renderZone = ({ item }: { item: Zone }) => (
    <View
      style={[
        styles.zoneCard,
        {
          backgroundColor: theme.colors.card,
          borderColor: theme.colors.border,
          flexDirection: isRTL ? 'row-reverse' : 'row',
        },
      ]}
    >
      <View style={[styles.zoneIcon, { backgroundColor: theme.colors.warning + '15' }]}>
        <Ionicons name="apps" size={24} color={theme.colors.warning} />
      </View>
      <View style={[styles.zoneInfo, isRTL && { alignItems: 'flex-end' }]}>
        <Text
          style={[
            styles.zoneName,
            { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
          ]}
        >
          {item.name}
        </Text>
        {item.site && (
          <View style={[styles.siteTag, { backgroundColor: theme.colors.info + '15' }]}>
            <Ionicons name="location" size={12} color={theme.colors.info} />
            <Text style={[styles.siteTagText, { color: theme.colors.info }]}>{item.site.name}</Text>
          </View>
        )}
        {item.description && (
          <Text
            style={[
              styles.zoneDescription,
              { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
            ]}
            numberOfLines={2}
          >
            {item.description}
          </Text>
        )}
      </View>
      <View style={[styles.actions]}>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: theme.colors.success + '15' }]}
          onPress={() =>
            navigation.navigate('ZonePermissions', { zoneId: item.id, zoneName: item.name })
          }
          accessible={true}
          accessibilityLabel={`${isArabic ? 'إدارة أذونات المنطقة' : 'Manage zone permissions'}: ${
            item.name
          }`}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لإدارة أذونات الوصول لهذه المنطقة'
              : 'Double tap to manage access permissions for this zone'
          }
          accessibilityRole="button"
        >
          <Ionicons name="people" size={18} color={theme.colors.success} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: theme.colors.primary + '15' }]}
          onPress={() => openModal(item)}
          accessible={true}
          accessibilityLabel={`${isArabic ? 'تعديل المنطقة' : 'Edit zone'}: ${item.name}`}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لتعديل تفاصيل المنطقة'
              : 'Double tap to edit zone details'
          }
          accessibilityRole="button"
        >
          <Ionicons name="create" size={18} color={theme.colors.primary} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: theme.colors.error + '15' }]}
          onPress={() => handleDelete(item)}
          accessible={true}
          accessibilityLabel={`${isArabic ? 'حذف المنطقة' : 'Delete zone'}: ${item.name}`}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لحذف المنطقة بشكل دائم. سيُطلب منك التأكيد'
              : 'Double tap to permanently delete zone. Confirmation will be required'
          }
          accessibilityRole="button"
        >
          <Ionicons name="trash" size={18} color={theme.colors.error} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const error = queryError ? (queryError as Error).message : null;

  if (isLoading && !isRefetching) {
    return <LoadingScreen message={isArabic ? 'جاري تحميل المناطق...' : 'Loading zones...'} />;
  }

  if (error && !isLoading && !isRefetching) {
    return (
      <ErrorView
        message={error}
        onRetry={() => refetch()}
        icon="apps-outline"
        title={isArabic ? 'فشل تحميل المناطق' : 'Failed to Load Zones'}
      />
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <FlatList
        data={zones}
        renderItem={renderZone}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} />}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons name="apps-outline" size={64} color={theme.colors.textSecondary} />
            <Text style={[styles.emptyText, { color: theme.colors.text }]}>
              {isArabic ? 'لا توجد مناطق' : 'No Zones'}
            </Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'اضغط + لإضافة منطقة جديدة' : 'Tap + to add a new zone'}
            </Text>
          </View>
        }
      />

      {/* Add/Edit Modal */}
      <Modal visible={showModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {editingZone
                  ? isArabic
                    ? 'تعديل المنطقة'
                    : 'Edit Zone'
                  : isArabic
                  ? 'إضافة منطقة'
                  : 'Add Zone'}
              </Text>
              <TouchableOpacity
                onPress={closeModal}
                accessible={true}
                accessibilityLabel={isArabic ? 'إغلاق' : 'Close'}
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لإغلاق النموذج بدون حفظ'
                    : 'Double tap to close form without saving'
                }
                accessibilityRole="button"
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                  textAlign: isRTL ? 'right' : 'left',
                },
              ]}
              placeholder={isArabic ? 'اسم المنطقة *' : 'Zone Name *'}
              placeholderTextColor={theme.colors.textSecondary}
              value={zoneName}
              onChangeText={setZoneName}
              accessible={true}
              accessibilityLabel={isArabic ? 'اسم المنطقة (مطلوب)' : 'Zone name (required)'}
              accessibilityHint={
                isArabic ? 'أدخل اسمًا فريدًا للمنطقة' : 'Enter a unique name for the zone'
              }
            />

            <TextInput
              style={[
                styles.input,
                styles.textArea,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                  textAlign: isRTL ? 'right' : 'left',
                },
              ]}
              placeholder={isArabic ? 'الوصف (اختياري)' : 'Description (optional)'}
              placeholderTextColor={theme.colors.textSecondary}
              value={zoneDescription}
              onChangeText={setZoneDescription}
              multiline
              numberOfLines={3}
              accessible={true}
              accessibilityLabel={
                isArabic ? 'وصف المنطقة (اختياري)' : 'Zone description (optional)'
              }
              accessibilityHint={
                isArabic ? 'أدخل وصفًا للمنطقة إن وجد' : 'Enter zone description if available'
              }
            />

            <Text
              style={[
                styles.label,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {isArabic ? 'الموقع *' : 'Site *'}
            </Text>
            <View
              style={[
                styles.pickerContainer,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
              ]}
              accessible={true}
              accessibilityLabel={
                selectedSiteId
                  ? `${isArabic ? 'الموقع' : 'Site'}: ${
                      sites.find((s) => s.id === selectedSiteId)?.name || ''
                    }`
                  : isArabic
                  ? 'الموقع: لم يتم الاختيار'
                  : 'Site: Not selected'
              }
              accessibilityRole="button"
              accessibilityHint={
                isArabic ? 'حدد الموقع الرئيسي لهذه المنطقة' : 'Select parent site for this zone'
              }
            >
              <Picker
                selectedValue={selectedSiteId}
                onValueChange={(value) => setSelectedSiteId(value)}
                style={{ color: theme.colors.text }}
                dropdownIconColor={theme.colors.text}
              >
                <Picker.Item label={isArabic ? 'اختر الموقع...' : 'Select Site...'} value="" />
                {sites.map((site) => (
                  <Picker.Item key={site.id} label={site.name} value={site.id} />
                ))}
              </Picker>
            </View>

            {sites.length === 0 && (
              <Text style={[styles.warningText, { color: theme.colors.warning }]}>
                {isArabic
                  ? 'يجب إضافة موقع أولاً قبل إضافة منطقة'
                  : 'You need to add a site first before adding a zone'}
              </Text>
            )}

            <TouchableOpacity
              style={[
                styles.saveButton,
                { backgroundColor: theme.colors.primary, opacity: sites.length === 0 ? 0.5 : 1 },
              ]}
              onPress={handleSave}
              disabled={saving || sites.length === 0}
              accessible={true}
              accessibilityLabel={
                saving
                  ? isArabic
                    ? 'جاري الحفظ...'
                    : 'Saving...'
                  : editingZone
                  ? isArabic
                    ? 'تحديث المنطقة'
                    : 'Update zone'
                  : isArabic
                  ? 'إضافة منطقة'
                  : 'Add zone'
              }
              accessibilityHint={
                saving
                  ? isArabic
                    ? 'يرجى الانتظار أثناء حفظ المنطقة'
                    : 'Please wait while zone is being saved'
                  : editingZone
                  ? isArabic
                    ? 'انقر نقرًا مزدوجًا لحفظ التغييرات'
                    : 'Double tap to save changes'
                  : isArabic
                  ? 'انقر نقرًا مزدوجًا لإنشاء منطقة جديدة'
                  : 'Double tap to create new zone'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: saving || sites.length === 0, busy: saving }}
            >
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {editingZone ? (isArabic ? 'تحديث' : 'Update') : isArabic ? 'إضافة' : 'Add'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 16,
    paddingBottom: 100,
  },
  zoneCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 14,
    marginBottom: 10,
    borderWidth: 1,
    gap: 12,
  },
  zoneIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoneInfo: {
    flex: 1,
    gap: 4,
  },
  zoneName: {
    fontSize: 16,
    fontWeight: '600',
  },
  siteTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  siteTagText: {
    fontSize: 11,
    fontWeight: '600',
  },
  zoneDescription: {
    fontSize: 13,
    marginTop: 4,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 20,
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  input: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    fontSize: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  pickerContainer: {
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
    overflow: 'hidden',
  },
  warningText: {
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 12,
  },
  saveButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },
});
